import React, { useEffect, useState } from "react";
import { DatePicker } from "antd";
import { Activity, StopCircle, Sliders } from "react-feather";
import Select from "react-select";
import dayjs from "dayjs";
import AuditLogServices from "../../services/AuditLog";
import { ErrorToaster } from "../components/toaster/Toaster";
import Table from "../../core/pagination/datatable";
import { useSearchParams } from "react-router-dom/dist";
import { cleanObject, Debounce } from "../../utils";
import { useSelector } from "react-redux";

const { RangePicker } = DatePicker;

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'VALIDATE', label: 'Validate' },
  { value: 'SUBMIT', label: 'Submit' },
  { value: 'BULK_VALIDATE', label: 'Bulk Validate' },
  { value: 'BULK_SUBMIT', label: 'Bulk Submit' },
  { value: 'BULK_DELETE', label: 'Bulk Delete' },
  { value: 'IMPORT', label: 'Import' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILURE', label: 'Failure' },
];

const ACTION_COLORS = {
  CREATE: { color: '#2563EB', bg: '#EFF6FF' },
  UPDATE: { color: '#D97706', bg: '#FFFBEB' },
  DELETE: { color: '#DC2626', bg: '#FEF2F2' },
  VALIDATE: { color: '#7C3AED', bg: '#F5F3FF' },
  SUBMIT: { color: '#059669', bg: '#ECFDF5' },
  BULK_VALIDATE: { color: '#7C3AED', bg: '#F5F3FF' },
  BULK_SUBMIT: { color: '#059669', bg: '#ECFDF5' },
  BULK_DELETE: { color: '#DC2626', bg: '#FEF2F2' },
  IMPORT: { color: '#0891B2', bg: '#ECFEFF' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-PK', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const AuditLogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loader, setLoader] = useState(false);
  const { userData } = useSelector((state) => ({ userData: state.user_detail }));

  const isAdmin = userData.role === 'admin';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageLimit = parseInt(searchParams.get('limit') || '10', 10);
  const action = searchParams.get('action') || '';
  const status = searchParams.get('status') || '';
  const startDate = searchParams.get('from') || '';
  const endDate = searchParams.get('to') || '';

  const getAuditLogs = async () => {
    setLoader(true);
    try {
      const params = { ...Object.fromEntries(searchParams.entries()), page: currentPage, limit: pageLimit };
      const { data } = await AuditLogServices.getAllAuditLogs(params);
      setLogs(data?.logs);
      setTotalCount(data?.pagination?.total);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const handleFilter = (data) => {
    const updated = { ...Object.fromEntries(searchParams.entries()), page: 1, ...cleanObject(data) };
    Debounce(() => setSearchParams(updated));
  };

  useEffect(() => { getAuditLogs(); }, [searchParams]);

  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'created_at',
      width: 160,
      render: (text) => <span style={{ fontSize: 13, color: '#374151' }}>{formatDate(text)}</span>,
    },
    ...(isAdmin ? [{
      title: 'User',
      dataIndex: 'user',
      render: (user) => user ? (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{user.name}</div>
          <div style={{ fontSize: 12, color: '#9CA3AF' }}>{user.email}</div>
        </div>
      ) : <span style={{ color: '#9CA3AF' }}>—</span>,
    }] : []),
    {
      title: 'Invoice Ref',
      dataIndex: 'invoice',
      render: (invoice) => invoice?.invoice_ref_no ? (
        <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{invoice.invoice_ref_no}</span>
      ) : <span style={{ color: '#9CA3AF' }}>—</span>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (text) => {
        const { color, bg } = ACTION_COLORS[text] || { color: '#6B7280', bg: '#F3F4F6' };
        const label = ACTION_OPTIONS.find((o) => o.value === text)?.label || text;
        return (
          <span style={{
            background: bg, color, fontWeight: 600, fontSize: 12,
            borderRadius: 12, padding: '3px 10px', whiteSpace: 'nowrap',
          }}>
            {label}
          </span>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text) => text === 'SUCCESS' ? (
        <span className="badge badge-linesuccess">Success</span>
      ) : (
        <span className="badge badge-linedanger">Failed</span>
      ),
    },
    {
      title: 'Details',
      dataIndex: 'details',
      render: (text) => (
        <div style={{ maxWidth: 280, fontSize: 13, color: '#6B7280', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {text || '—'}
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Audit Logs</h4>
            <h6>{isAdmin ? 'Track all actions performed across all users' : 'Your activity history'}</h6>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            {/* Filters */}
            <div className="table-top" style={{ display: 'block', marginBottom: 10, padding: '18px 18px 0' }}>
              <div className="d-flex align-items-flex-end gap-3 flex-wrap">
                <div>
                  <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Action</label>
                  <div className="input-blocks mb-0">
                    <Activity className="info-img" />
                    <Select
                      classNamePrefix="react-select"
                      className="img-select"
                      options={ACTION_OPTIONS}
                      value={ACTION_OPTIONS.find((o) => o.value === action) || ACTION_OPTIONS[0]}
                      onChange={(e) => handleFilter({ action: e.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Status</label>
                  <div className="input-blocks mb-0">
                    <StopCircle className="info-img" />
                    <Select
                      classNamePrefix="react-select"
                      className="img-select"
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0]}
                      onChange={(e) => handleFilter({ status: e.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Date Range</label>
                  <div className="input-blocks mb-0">
                    <div className="input-groupicon calender-input">
                      <Sliders className="info-img" />
                      <RangePicker
                        value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
                        onChange={(date) => handleFilter({
                          from: date ? dayjs(date[0]).format('YYYY-MM-DD') : '',
                          to: date ? dayjs(date[1]).format('YYYY-MM-DD') : '',
                        })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <Table
                loading={loader}
                columns={columns}
                dataSource={logs}
                totalCount={totalCount}
                pageSize={pageLimit}
                currentPage={currentPage}
                onChange={(page, pageSize) => handleFilter({ page, limit: pageSize })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
